import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import NumericKeypadModal from "../../components/common/NumericKeypadModal";
import OnScreenKeyboard from "../../components/common/OnScreenKeyboard";
import Select from "../../components/common/Select";
import Toast from "../../components/common/Toast";
import Textarea from "../../components/common/Textarea";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import SummaryCard from "../../components/pos/SummaryCard";
import {
  useCreateCashMovementMutation,
  useDeleteCashMovementMutation,
  useGetCashMovementsQuery,
  useGetOpenTillSessionsQuery,
  useGetReconciliationQuery,
  useGetTillSessionQuery,
  useGetTillsQuery,
  useUpdateCashMovementMutation,
} from "../../store/api";
import useCurrentOperator from "../../hooks/useCurrentOperator";
import { usePosRealtime } from "../../hooks/usePosRealtime";
import {
  formatCurrency,
  formatSignedCurrency,
  getCashingUpMovementTone,
  getCashingUpMovementTypeLabel,
  getMovementSignedAmount,
  getTenderSummaryAmount,
  isVisibleCashMovementStatus,
} from "../../utils/cashingUp";
import {
  getCashSessionDeviceName,
  useDeviceCashSessionResolution,
} from "../../hooks/useDeviceCashSession";

const getErrorMessage = (err, fallback) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.message ||
  fallback ||
  "Something went wrong.";

const movementInitialForm = {
  movementType: "PAID_OUT",
  amount: "",
  reasonCode: "",
  notes: "",
  attachmentRef: "",
  changeReason: "",
};

const CASH_MOVEMENT_OPTIONS = ["PAID_OUT", "CASH_DROP", "TIP_PAYOUT"];

const IconButton = ({ children, className = "", ...props }) => (
  <button
    type="button"
    className={[
      "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-100",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    {...props}
  >
    {children}
  </button>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
    <path
      d="M12 5v14M5 12h14"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
    <path
      d="M12 20h9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
    <path
      d="M3 6h18"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M8 6V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M19 6l-1 13.2A1.8 1.8 0 0 1 16.2 21H7.8A1.8 1.8 0 0 1 6 19.2L5 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M10 10v6M14 10v6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const getDirectionLabel = (signedAmount) => {
  if (signedAmount > 0) return "In";
  if (signedAmount < 0) return "Out";
  return "Neutral";
};

const CashMovements = ({
  embedded = false,
  selectedSessionId: selectedSessionIdProp,
  onContinueToCashUp,
} = {}) => {
  const navigate = useNavigate();
  const { selectedStoreId } = useStore();
  const user = useSelector((state) => state.auth.user);
  const { userId: currentOperatorUserId } = useCurrentOperator();

  const isSelectedSessionControlled =
    selectedSessionIdProp !== undefined && selectedSessionIdProp !== null;
  const [internalSelectedSessionId, setInternalSelectedSessionId] = useState("");
  const selectedSessionId = isSelectedSessionControlled
    ? selectedSessionIdProp
    : internalSelectedSessionId;
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [movementForm, setMovementForm] = useState(movementInitialForm);
  const [movementFormErrors, setMovementFormErrors] = useState({});
  const [movementError, setMovementError] = useState("");
  const [toast, setToast] = useState(null);
  const [deleteMovementError, setDeleteMovementError] = useState("");
  const [movementFilter, setMovementFilter] = useState("all");
  const [movementSearch, setMovementSearch] = useState("");
  const [editingMovementId, setEditingMovementId] = useState(null);
  const [movementToDelete, setMovementToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [numericPadOpen, setNumericPadOpen] = useState(false);
  const [textPadOpen, setTextPadOpen] = useState(false);
  const [activeTextField, setActiveTextField] = useState("");
  const movementReasonInputRef = useRef(null);
  const deleteReasonInputRef = useRef(null);

  const {
    data: openSessionsData,
    isLoading: sessionsLoading,
    isError: sessionsIsError,
    error: sessionsError,
    refetch: refetchSessions,
  } = useGetOpenTillSessionsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });

  const openSessions = useMemo(
    () => (Array.isArray(openSessionsData) ? openSessionsData : []),
    [openSessionsData],
  );

  const deviceSessionResolution = useDeviceCashSessionResolution(
    openSessions,
    Boolean(selectedStoreId),
  );

  useEffect(() => {
    if (isSelectedSessionControlled) {
      return;
    }
    if (!selectedStoreId) {
      setInternalSelectedSessionId("");
      return;
    }
    if (sessionsLoading || deviceSessionResolution.isLoading) {
      return;
    }

    if (deviceSessionResolution.status === "ready") {
      setInternalSelectedSessionId(deviceSessionResolution.selectedSessionId);
      return;
    }

    setInternalSelectedSessionId("");
  }, [
    deviceSessionResolution.isLoading,
    deviceSessionResolution.selectedSessionId,
    deviceSessionResolution.status,
    isSelectedSessionControlled,
    sessionsLoading,
    selectedStoreId,
  ]);

  const {
    data: sessionData,
    isError: sessionIsError,
    error: sessionError,
    refetch: refetchSession,
  } = useGetTillSessionQuery(selectedSessionId, {
    skip: !selectedSessionId,
  });

  const session = sessionData && typeof sessionData === "object" ? sessionData : null;

  const {
    data: reconciliationData,
    refetch: refetchReconciliation,
  } = useGetReconciliationQuery(selectedSessionId, {
    skip: !selectedSessionId,
  });

  const { data: tillsData } = useGetTillsQuery(
    { storeId: session?.storeId || selectedStoreId, activeOnly: true },
    { skip: !selectedStoreId && !session?.storeId },
  );

  const tills = useMemo(
    () => (Array.isArray(tillsData) ? tillsData : []),
    [tillsData],
  );
  const tillById = useMemo(
    () => new Map(tills.map((till) => [till.tillId, till])),
    [tills],
  );
  const currentDeviceName = getCashSessionDeviceName(
    deviceSessionResolution.device,
  );
  const tillDisplay = useMemo(() => {
    if (deviceSessionResolution.status === "no_session") {
      return {
        title: "No active session",
        subtitle: "",
      };
    }

    if (!selectedSessionId) {
      return {
        title: "Session unavailable",
        subtitle: deviceSessionResolution.message || "Select a valid session to continue.",
      };
    }

    return {
      title:
        currentDeviceName ||
        session?.deviceName ||
        session?.deviceKey ||
        session?.deviceToken ||
        tillById.get(session?.tillId)?.tillName ||
        tillById.get(session?.tillId)?.tillCode ||
        session?.tillName ||
        session?.tillCode ||
        "Till unavailable",
      subtitle: "",
    };
  }, [
    deviceSessionResolution.message,
    deviceSessionResolution.status,
    currentDeviceName,
    selectedSessionId,
    session,
    tillById,
  ]);

  const currentUserId = user?.userId || user?.id || currentOperatorUserId || null;

  const {
    data: movementsData,
    isLoading: movementsLoading,
    isError: movementsIsError,
    error: movementsError,
    refetch: refetchMovements,
  } = useGetCashMovementsQuery(selectedSessionId, {
    skip: !selectedSessionId,
  });
  const [createCashMovement, createMovementState] =
    useCreateCashMovementMutation();
  const [updateCashMovement, updateMovementState] =
    useUpdateCashMovementMutation();
  const [deleteCashMovement, deleteMovementState] =
    useDeleteCashMovementMutation();

  const handleCashSessionChanged = useCallback(
    (payload) => {
      const changedSessionId = payload?.cashSessionId || payload?.sessionId;
      if (!changedSessionId || changedSessionId === selectedSessionId) {
        refetchSession();
        refetchMovements();
        refetchReconciliation();
      }
      refetchSessions();
    },
    [
      refetchMovements,
      refetchReconciliation,
      refetchSession,
      refetchSessions,
      selectedSessionId,
    ],
  );

  usePosRealtime({
    onCashSessionChanged: handleCashSessionChanged,
  });

  useEffect(() => {
    setMovementFilter("all");
    setMovementSearch("");
    setMovementModalOpen(false);
    setMovementToDelete(null);
    setDeleteReason("");
    setMovementError("");
    setToast(null);
    setEditingMovementId(null);
    setMovementForm(movementInitialForm);
    setMovementFormErrors({});
  }, [selectedSessionId]);

  const visibleMovements = useMemo(
    () =>
      (Array.isArray(movementsData) ? movementsData : []).filter((movement) =>
        isVisibleCashMovementStatus(movement?.status),
      ),
    [movementsData],
  );

  const normalizedMovements = useMemo(
    () =>
      visibleMovements.map((movement) => {
        const signedAmount = getMovementSignedAmount(
          movement.movementType,
          movement.amount,
        );
        return {
          ...movement,
          signedAmount,
          direction: getDirectionLabel(signedAmount),
        };
      }),
    [visibleMovements],
  );

  const selectedMovementBeingEdited = useMemo(
    () =>
      editingMovementId
        ? normalizedMovements.find(
            (movement) => movement.cashMovementId === editingMovementId,
          ) || null
        : null,
    [editingMovementId, normalizedMovements],
  );

  const filteredMovements = useMemo(() => {
    const searchTerm = movementSearch.trim().toLowerCase();

    return normalizedMovements.filter((movement) => {
      const typeMatches =
        movementFilter === "all" ||
        String(movement.movementType || "").toLowerCase() === movementFilter;
      if (!typeMatches) return false;

      if (!searchTerm) return true;

      const haystack = [
        movement.movementType,
        movement.reasonCode,
        movement.notes,
        movement.direction,
        movement.createdByUserId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(searchTerm);
    });
  }, [movementFilter, movementSearch, normalizedMovements]);

  const totalMovementImpact = useMemo(
    () =>
      normalizedMovements.reduce(
        (total, movement) => total + Number(movement.signedAmount || 0),
        0,
      ),
    [normalizedMovements],
  );

  const systemCashSales = Number(
    reconciliationData?.expectedCash?.cashPayments || 0,
  );
  const systemCardSales = Number(
    session?.cardSales || getTenderSummaryAmount(reconciliationData, "CARD"),
  );
  const openingCash = Number(
    reconciliationData?.expectedCash?.openingCash || session?.openingCash || 0,
  );
  const cashRefunds = Number(
    session?.cashRefunds ?? reconciliationData?.expectedCash?.cashRefunds ?? 0,
  );
  const expectedDrawerCash = Number(
    openingCash + systemCashSales - cashRefunds + totalMovementImpact,
  );
  const movementEntryLimit = useMemo(() => {
    const currentDrawerCash = Number.isFinite(expectedDrawerCash)
      ? expectedDrawerCash
      : 0;
    if (!selectedMovementBeingEdited) {
      return currentDrawerCash;
    }

    const existingImpact = Number(selectedMovementBeingEdited.signedAmount || 0);
    return currentDrawerCash - existingImpact;
  }, [expectedDrawerCash, selectedMovementBeingEdited]);
  const tillLabel = useMemo(() => {
    const till = tillById.get(session?.tillId);
    return till?.tillName || till?.tillCode || session?.tillName || session?.tillCode || "Unknown";
  }, [session, tillById]);

  const netMovementLabel = formatSignedCurrency(totalMovementImpact);
  const cardRefunds = Number(
    session?.cardRefunds ?? reconciliationData?.expectedCash?.cardRefunds ?? 0,
  );

  const resetMovementForm = useCallback(() => {
    setMovementForm(movementInitialForm);
    setMovementFormErrors({});
    setMovementError("");
    setEditingMovementId(null);
    setNumericPadOpen(false);
    setTextPadOpen(false);
    setActiveTextField("");
  }, []);

  const openAddMovementModal = useCallback(() => {
    resetMovementForm();
    setMovementModalOpen(true);
  }, [resetMovementForm]);

  const openEditMovementModal = useCallback((movement) => {
    if (!movement) return;
    setEditingMovementId(movement.cashMovementId);
    setMovementForm({
      movementType: movement.movementType || "PAID_OUT",
      amount:
        movement.amount !== undefined && movement.amount !== null
          ? String(Math.abs(Number(movement.amount)))
          : "",
      reasonCode: movement.reasonCode || "",
      notes: movement.notes || "",
      attachmentRef: movement.attachmentRef || "",
      changeReason: "",
    });
    setMovementFormErrors({});
    setMovementError("");
    setMovementModalOpen(true);
    setNumericPadOpen(false);
  }, []);

  const movementAmountValue = movementForm.amount || "";

  const handleMovementAmountPadChange = (nextValue) => {
    setMovementForm((prev) => ({ ...prev, amount: nextValue }));
    setMovementFormErrors((prev) => ({ ...prev, amount: "" }));
    setMovementError("");
    setToast(null);
  };

  const openTextKeyboard = (fieldName) => {
    setActiveTextField(fieldName);
    setTextPadOpen(true);
    setNumericPadOpen(false);
  };

  const handleTextKeyboardChange = (nextValue) => {
    if (activeTextField === "reasonCode") {
      setMovementForm((prev) => ({ ...prev, reasonCode: nextValue }));
      setMovementFormErrors((prev) => ({ ...prev, reasonCode: "" }));
      setMovementError("");
      setToast(null);
      return;
    }

    if (activeTextField === "deleteReason") {
      setDeleteReason(nextValue);
    }
  };

  const handleTextKeyboardDone = () => {
    setTextPadOpen(false);
    setActiveTextField("");
  };

  const handleMovementChange = (e) => {
    const { name, value } = e.target;
    setMovementFormErrors({});
    setMovementError("");
    setMovementForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateMovementForm = () => {
    const errors = {};
    if (!movementForm.movementType) {
      errors.movementType = "Movement type is required.";
    }
    if (movementForm.amount === "" || Number.isNaN(Number(movementForm.amount))) {
      errors.amount = "Amount must be a valid number.";
    } else if (Number(movementForm.amount) <= 0) {
      errors.amount = "Amount must be greater than zero.";
    }

    const movementAmount = Number(movementForm.amount);
    const movementImpact = getMovementSignedAmount(
      movementForm.movementType,
      movementAmount,
    );
    if (
      Number.isFinite(movementAmount) &&
      movementImpact < 0 &&
      movementAmount > movementEntryLimit
    ) {
      errors.amount = `Amount cannot exceed ${formatCurrency(movementEntryLimit)} because only that much cash remains in the drawer.`;
    }
    return errors;
  };

  const submitMovement = async (e) => {
    e.preventDefault();
    if (!selectedSessionId) {
      setMovementError("Select a session first.");
      return;
    }

    const errors = validateMovementForm();
    if (Object.keys(errors).length > 0) {
      setMovementFormErrors(errors);
      return;
    }

    const movementAmount = Number(movementForm.amount);
    const movementImpact = getMovementSignedAmount(
      movementForm.movementType,
      movementAmount,
    );
    if (movementImpact < 0 && movementAmount > movementEntryLimit) {
      setMovementFormErrors({
        amount: `Amount cannot exceed ${formatCurrency(movementEntryLimit)} because only that much cash remains in the drawer.`,
      });
      return;
    }

    const payload = {
      movementType: movementForm.movementType,
      amount: movementAmount,
      tillId: session?.tillId || null,
      reasonCode: movementForm.reasonCode.trim() || null,
      notes: movementForm.notes.trim() || null,
      attachmentRef: movementForm.attachmentRef.trim() || null,
      createdByUserId: currentUserId || null,
    };

    if (editingMovementId) {
      try {
        await updateCashMovement({
          cashMovementId: editingMovementId,
          payload: {
            tillId: session?.tillId || null,
            movementType: movementForm.movementType,
            amount: movementAmount,
            reasonCode: movementForm.reasonCode.trim() || null,
            notes: movementForm.notes.trim() || null,
            attachmentRef: movementForm.attachmentRef.trim() || null,
            updatedByUserId: currentUserId || null,
            changeReason: movementForm.changeReason.trim() || null,
          },
        }).unwrap();
        setMovementModalOpen(false);
        resetMovementForm();
        setToast({
          message: "Movement updated.",
          tone: "success",
          duration: 3000,
        });
        refetchMovements();
        refetchSession();
        refetchSessions();
        refetchReconciliation();
      } catch (err) {
        setMovementError(getErrorMessage(err, "Failed to update cash movement."));
      }
      return;
    }

    try {
      await createCashMovement({
        cashSessionId: selectedSessionId,
        payload,
      }).unwrap();
      setMovementModalOpen(false);
      resetMovementForm();
      setToast({
        message: "Cash movement saved.",
        tone: "success",
        duration: 3000,
      });
      refetchMovements();
      refetchSession();
      refetchSessions();
      refetchReconciliation();
    } catch (err) {
      setMovementError(getErrorMessage(err, "Failed to save cash movement."));
    }
  };

  const handleDeleteMovement = (movement) => {
    setMovementToDelete(movement || null);
    setDeleteReason("");
    setDeleteMovementError("");
  };

  const confirmDeleteMovement = async () => {
    if (!movementToDelete?.cashMovementId) return;
    try {
      await deleteCashMovement({
        cashMovementId: movementToDelete.cashMovementId,
        deletedByUserId: currentUserId || null,
        reason: deleteReason.trim() || null,
      }).unwrap();
      setMovementToDelete(null);
      setDeleteReason("");
      setToast({
        message: "Movement deleted.",
        tone: "success",
        duration: 3000,
      });
      refetchMovements();
      refetchSession();
      refetchSessions();
      refetchReconciliation();
    } catch (err) {
      setDeleteMovementError(
        getErrorMessage(err, "Failed to delete cash movement."),
      );
    }
  };

  const columns = [
    { key: "dateTime", label: "Date & Time" },
    { key: "movementType", label: "Type" },
    { key: "amount", label: "Amount" },
    { key: "direction", label: "Direction" },
    { key: "reasonCode", label: "Reason" },
    { key: "actions", label: "Actions" },
  ];

  const rows = filteredMovements.map((movement) => {
    const signedAmount = movement.signedAmount || 0;
    const amountTone =
      signedAmount > 0
        ? "text-emerald-700 dark:text-emerald-300"
        : signedAmount < 0
          ? "text-rose-700 dark:text-rose-300"
          : "text-slate-700 dark:text-slate-300";

    return {
      id: movement.cashMovementId,
      dateTime: movement.createdAt
        ? new Date(movement.createdAt).toLocaleString()
        : movement.updatedAt
          ? new Date(movement.updatedAt).toLocaleString()
          : "-",
      movementType: (
        <Badge tone={getCashingUpMovementTone(movement.movementType)}>
          {getCashingUpMovementTypeLabel(movement.movementType)}
        </Badge>
      ),
      amount: (
        <span className={`font-semibold ${amountTone}`}>
          {formatSignedCurrency(signedAmount)}
        </span>
      ),
      direction: (
        <span className="inline-flex items-center gap-2 font-medium text-slate-700 dark:text-slate-300">
          <span
            className={[
              "inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold",
              signedAmount > 0
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200"
                : signedAmount < 0
                  ? "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-200"
                  : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {signedAmount > 0 ? "+" : signedAmount < 0 ? "−" : "•"}
          </span>
          {movement.direction}
        </span>
      ),
      reasonCode: movement.reasonCode || "-",
      actions: (
        <div className="flex items-center gap-2">
          <IconButton
            aria-label="Edit movement"
            title="Edit"
            onClick={() => openEditMovementModal(movement)}
          >
            <PencilIcon />
          </IconButton>
          <IconButton
            aria-label="Delete movement"
            title="Delete"
            disabled={String(movement.status || "").toUpperCase() !== "PENDING"}
            className="text-rose-600 hover:text-rose-700 dark:text-rose-300"
            onClick={() => handleDeleteMovement(movement)}
          >
            <TrashIcon />
          </IconButton>
        </div>
      ),
    };
  });

  const handleContinue = () => {
    if (typeof onContinueToCashUp === "function") {
      onContinueToCashUp();
      return;
    }
    navigate("/pos/cash-up");
  };

  const handleViewShiftHistory = () => {
    const params = new URLSearchParams();
    if (session?.businessDate) {
      params.set("fromDate", session.businessDate);
      params.set("toDate", session.businessDate);
    }
    if (session?.tillId) params.set("tillId", session.tillId);
    if (selectedSessionId) params.set("sessionId", selectedSessionId);
    navigate(
      `/pos/cash-movements/history${params.toString() ? `?${params.toString()}` : ""}`,
    );
  };

  const content = (
    <div className="space-y-4 px-4 pb-4 pt-2 sm:px-6 lg:px-8 lg:pt-3">
      {!embedded && (
        <PageHeader
          titleClassName="!text-xl"
          subtitle="Record expenses, refunds, and cash drops for the active session."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleViewShiftHistory}
              >
                Shift History
              </Button>
            </div>
          }
        />
      )}

      {!selectedStoreId && (
        <Card className="border-slate-200 bg-white/95 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
          <Alert tone="warning">No store is selected. Choose a store first.</Alert>
        </Card>
      )}

      {selectedStoreId && (
        <div className="space-y-4">
          {sessionsLoading && <Alert tone="info">Loading open sessions...</Alert>}
          {sessionsIsError && (
            <Alert tone="error">
              {getErrorMessage(sessionsError, "Failed to load open sessions.")}
            </Alert>
          )}
          {sessionIsError && (
            <Alert tone="error">
              {getErrorMessage(sessionError, "Failed to load session details.")}
            </Alert>
          )}
          {movementsIsError && (
            <Alert tone="error">
              {getErrorMessage(movementsError, "Failed to load cash movements.")}
            </Alert>
          )}
          {movementError && <Alert tone="error">{movementError}</Alert>}
          {deviceSessionResolution.status === "no_session" && (
            <Alert tone="warning">{deviceSessionResolution.message}</Alert>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
            <SummaryCard
              label="Till"
              value={tillDisplay.title}
              tone="violet"
              compact
            />
            <SummaryCard
              label="Starting Cash"
              value={formatCurrency(openingCash)}
              tone="amber"
              compact
            />
            <SummaryCard
              label="Cash Sales"
              value={formatCurrency(systemCashSales)}
              tone="emerald"
              compact
            />
            <SummaryCard
              label="Cash Refunds"
              value={formatCurrency(cashRefunds)}
              tone="rose"
              compact
            />
            <SummaryCard
              label="PDQ Sales"
              value={formatCurrency(systemCardSales)}
              tone="blue"
              compact
            />
            <SummaryCard
              label="PDQ Refunds"
              value={formatCurrency(cardRefunds)}
              tone="rose"
              compact
            />
            {/*
            <SummaryCard
              label="PDQ Tips"
              value={formatCurrency(systemCardTips)}
              tone="rose"
              compact
            />
            */}
            <SummaryCard
              label="Cash Left in Drawer"
              value={formatCurrency(expectedDrawerCash)}
              tone="indigo"
              compact
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
            <Card className="overflow-hidden border-slate-200 bg-white/95 p-0 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
              <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between md:px-5 dark:border-slate-700">
                <div className="space-y-0.5">
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    Movements
                  </h2>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="pos-primary"
                    onClick={openAddMovementModal}
                    disabled={!selectedSessionId}
                  >
                    <span className="inline-flex items-center gap-2 whitespace-nowrap">
                      <PlusIcon />
                      Add Movement
                    </span>
                  </Button>
                </div>
              </div>

              <div className="space-y-3 px-4 py-3 md:px-5">
                <div className="grid gap-3 xl:grid-cols-[170px_minmax(0,1fr)] xl:items-start">
                  <div className="relative">
                    <select
                      value={movementFilter}
                      onChange={(e) => setMovementFilter(e.target.value)}
                      className="h-11 w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 pr-10 text-base text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-500"
                    >
                      <option value="all">All Types</option>
                      {CASH_MOVEMENT_OPTIONS.map((type) => (
                        <option key={type} value={String(type).toLowerCase()}>
                          {getCashingUpMovementTypeLabel(type)}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </span>
                  </div>

                  <div className="relative w-full justify-self-stretch">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </span>
                    <input
                      type="text"
                      value={movementSearch}
                      onChange={(e) => setMovementSearch(e.target.value)}
                      placeholder="Search movements..."
                      className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                  </div>
                </div>

                {movementsLoading && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Loading movements...
                  </p>
                )}

                {!movementsLoading && !movementsIsError && (
                  <Table
                    columns={columns}
                    rows={rows}
                    emptyText="No cash movements recorded for this session."
                  />
                )}
              </div>
            </Card>

            <div className="space-y-4">
              <Card className="border-slate-200 bg-white/95 p-4 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                      Cash Left in Drawer
                    </h3>
                  </div>
                  <div className="rounded-full bg-violet-50 p-2.5 text-violet-600 dark:bg-violet-950/40 dark:text-violet-200">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 19V5m0 14h16M8 15l3-3 2 2 5-6"
                      />
                    </svg>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-600 dark:text-slate-400">Start</span>
                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                        {formatCurrency(openingCash)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-600 dark:text-slate-400">Sales</span>
                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                        + {formatCurrency(systemCashSales)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-600 dark:text-slate-400">Cash Refunds</span>
                      <span className="font-semibold text-rose-600 dark:text-rose-300">
                        {formatSignedCurrency(-cashRefunds)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-600 dark:text-slate-400">Movements</span>
                      <span
                        className={`font-semibold ${
                          totalMovementImpact >= 0
                            ? "text-emerald-600 dark:text-emerald-300"
                            : "text-rose-600 dark:text-rose-300"
                        }`}
                      >
                        {netMovementLabel}
                      </span>
                    </div>
                    <div className="my-2 border-t border-slate-200 dark:border-slate-700" />
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Cash Left
                      </span>
                      <span className="text-lg font-bold text-indigo-600 dark:text-indigo-300">
                        {formatCurrency(expectedDrawerCash)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button size="sm" variant="pos-primary" onClick={handleContinue}>
                    <span className="inline-flex items-center gap-2">
                      Continue to Cash Up
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 12h14m-6-6l6 6-6 6"
                        />
                      </svg>
                    </span>
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {toast ? (
        <Toast
          message={toast.message}
          tone={toast.tone}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      ) : null}
      {content}
      {!isSelectedSessionControlled &&
        !sessionsLoading &&
        !deviceSessionResolution.isLoading &&
        deviceSessionResolution.status !== "ready" &&
        deviceSessionResolution.status !== "no_session" && (
          <div className="px-4 pb-2 md:px-6 lg:px-8">
            <Alert tone="warning">{deviceSessionResolution.message}</Alert>
          </div>
        )}

      <NumericKeypadModal
        isOpen={numericPadOpen && movementModalOpen}
        title="Movement Amount"
        value={movementAmountValue}
        onChange={handleMovementAmountPadChange}
        onClose={() => setNumericPadOpen(false)}
      />

      <Modal
        isOpen={movementModalOpen}
        onClose={() => {
          setNumericPadOpen(false);
          setMovementModalOpen(false);
          resetMovementForm();
        }}
        title={editingMovementId ? "Edit Movement" : "Add Movement"}
        confirmText={
          createMovementState.isLoading || updateMovementState.isLoading
            ? "Saving..."
            : editingMovementId
              ? "Update Movement"
              : "Save Movement"
        }
        onConfirm={submitMovement}
        isLoading={createMovementState.isLoading || updateMovementState.isLoading}
        size="xl"
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              label="Movement Type"
              name="movementType"
              value={movementForm.movementType}
              onChange={handleMovementChange}
              error={movementFormErrors.movementType}
              required
            >
              {CASH_MOVEMENT_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {getCashingUpMovementTypeLabel(type)}
                </option>
              ))}
            </Select>
            <Input
              label="Amount"
              name="amount"
              type="number"
              min="0"
              step="0.01"
              autoComplete="off"
              value={movementForm.amount}
              onChange={handleMovementChange}
              onFocus={() => setNumericPadOpen(true)}
              onClick={() => setNumericPadOpen(true)}
              error={movementFormErrors.amount}
              helper={`Available drawer cash: ${formatCurrency(movementEntryLimit)}.`}
              required
            />
          </div>

          <Input
            label="Reason"
            name="reasonCode"
            ref={movementReasonInputRef}
            value={movementForm.reasonCode}
            onChange={handleMovementChange}
            onFocus={() => openTextKeyboard("reasonCode")}
            onClick={() => openTextKeyboard("reasonCode")}
            autoComplete="off"
            spellCheck={false}
          />

        </div>
      </Modal>

      <Modal
        isOpen={movementModalOpen && textPadOpen && activeTextField === "reasonCode"}
        onClose={handleTextKeyboardDone}
        title="Movement Reason"
        size="xl"
        align="center"
        zIndexClassName="z-[60]"
        showCancel={false}
        bodyClassName="p-4"
      >
        <div className="space-y-4">
          <Input
            label="Reason"
            value={movementForm.reasonCode}
            ref={movementReasonInputRef}
            className="bg-slate-50 dark:bg-slate-800"
            onChange={handleMovementChange}
            name="reasonCode"
            autoComplete="off"
            spellCheck={false}
          />
          <OnScreenKeyboard
            layout="alpha"
            size="md"
            value={movementForm.reasonCode}
            onChange={handleTextKeyboardChange}
            inputRef={movementReasonInputRef}
            onDone={handleTextKeyboardDone}
            title="Movement Reason"
            className="shadow-none"
          />
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(movementToDelete)}
        onClose={() => {
          setMovementToDelete(null);
          setDeleteReason("");
          setDeleteMovementError("");
          setTextPadOpen(false);
          setActiveTextField("");
        }}
        title="Delete Movement"
        confirmText={deleteMovementState.isLoading ? "Deleting..." : "Delete"}
        onConfirm={confirmDeleteMovement}
        isLoading={deleteMovementState.isLoading}
      >
        <div className="space-y-3">
          <Alert tone="warning">
            Remove this movement from the current session view?
          </Alert>
          {deleteMovementError && (
            <Alert tone="error">{deleteMovementError}</Alert>
          )}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-200">
            <p className="font-semibold">
              {movementToDelete
                ? getCashingUpMovementTypeLabel(movementToDelete.movementType)
                : "-"}
            </p>
            <p className="mt-1">
              {movementToDelete
                ? formatSignedCurrency(
                    getMovementSignedAmount(
                      movementToDelete.movementType,
                      movementToDelete.amount,
                    ),
                  )
                : "-"}
            </p>
          </div>
          <Textarea
            label="Delete Reason"
            ref={deleteReasonInputRef}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            rows={3}
            onFocus={() => openTextKeyboard("deleteReason")}
            onClick={() => openTextKeyboard("deleteReason")}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(movementToDelete) && textPadOpen && activeTextField === "deleteReason"}
        onClose={handleTextKeyboardDone}
        title="Delete Reason"
        size="xl"
        align="center"
        zIndexClassName="z-[60]"
        showCancel={false}
        bodyClassName="p-4"
      >
        <div className="space-y-4">
          <Input
            label="Delete Reason"
            value={deleteReason}
            ref={deleteReasonInputRef}
            className="bg-slate-50 dark:bg-slate-800"
            name="deleteReason"
            onChange={(e) => setDeleteReason(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <OnScreenKeyboard
            layout="alpha"
            size="md"
            value={deleteReason}
            onChange={handleTextKeyboardChange}
            inputRef={deleteReasonInputRef}
            onDone={handleTextKeyboardDone}
            title="Delete Reason"
            className="shadow-none"
          />
        </div>
      </Modal>

    </>
  );
};

export default CashMovements;
